/* =====================================================================
 * Sidebar favorites storage helpers
 *
 * Thin wrappers around `localStorage` for persisting a user's pinned
 * workspace modes between sessions. Extracted from Sidebar.tsx so the
 * pure storage logic can be unit-tested in isolation and reused by
 * future sidebar variants (compact sidebar, mobile rail, etc.).
 * ===================================================================== */

import { activeTenantKey } from '@auw/tenant';

import type { WorkspaceMode } from '../types/agent';
import { WORKSPACE_MODES, WORKSPACE_ORDER } from './workspaceMode';

/* Resolved lazily on first reader so this module body can evaluate
 * before the host's tenant bootstrap has registered the active
 * manifest. Vite/Rollup are free to hoist this module into a chunk
 * that ends up evaluated alongside the entry — under those conditions
 * a top-level `activeTenantKey(...)` would throw because the registry
 * is still empty. Caching after first read is safe: tenant identity
 * is fixed for the page lifetime. */
let _sidebarFavoritesStorageKey: string | null = null;
function getSidebarFavoritesStorageKeyPrefix(): string {
  if (_sidebarFavoritesStorageKey === null) {
    _sidebarFavoritesStorageKey = activeTenantKey('sidebar_favorite_modes');
  }
  return _sidebarFavoritesStorageKey;
}

/**
 * Core workspace modes rendered as the top-level navigation, independent
 * of user favorites. Backed by the default WORKSPACE_ORDER so the set
 * stays in sync with the registry.
 */
const CORE_WORKSPACE_MODE_SET = new Set<WorkspaceMode>(WORKSPACE_ORDER);

function canUseSidebarStorage(): boolean {
  return typeof window !== 'undefined' && typeof localStorage !== 'undefined';
}

function getSidebarFavoritesStorageKey(scope: string): string {
  return `${getSidebarFavoritesStorageKeyPrefix()}:${scope || 'anonymous'}`;
}

/**
 * Filter `raw` down to known workspace modes, dedup, and preserve order.
 * Silently drops unknown / duplicate entries so stale localStorage data
 * from renamed modes never crashes the sidebar.
 */
export function normalizeSidebarFavoriteModes(raw: unknown): WorkspaceMode[] {
  if (!Array.isArray(raw)) return [];
  const validModes = new Set<WorkspaceMode>(
    Object.keys(WORKSPACE_MODES) as WorkspaceMode[],
  );
  const seen = new Set<WorkspaceMode>();
  const normalized: WorkspaceMode[] = [];

  for (const item of raw) {
    if (typeof item !== 'string') continue;
    const mode = item as WorkspaceMode;
    if (!validModes.has(mode) || seen.has(mode)) continue;
    seen.add(mode);
    normalized.push(mode);
  }

  return normalized;
}

export function readSidebarFavoriteModes(scope: string): WorkspaceMode[] {
  if (!canUseSidebarStorage()) return [];
  try {
    const raw = localStorage.getItem(getSidebarFavoritesStorageKey(scope));
    return normalizeSidebarFavoriteModes(raw ? JSON.parse(raw) : []);
  } catch {
    return [];
  }
}

export function writeSidebarFavoriteModes(
  scope: string,
  modes: WorkspaceMode[],
): void {
  if (!canUseSidebarStorage()) return;
  localStorage.setItem(
    getSidebarFavoritesStorageKey(scope),
    JSON.stringify(normalizeSidebarFavoriteModes(modes)),
  );
}

export function isCoreWorkspaceMode(mode: WorkspaceMode): boolean {
  return CORE_WORKSPACE_MODE_SET.has(mode);
}
